// Recently viewed products, stored in the shopper's own browser (localStorage, newest first, max 12).
//   <s-recently-viewed-track data-handle="..."> — on product pages: records the view.
//   <s-recently-viewed data-exclude="h1,h2" data-limit="8"> — on the cart page: fetches a card for each
//     remembered product (/products/<handle>?view=recent-card), skips items in the cart and sold-out ones,
//     and reveals the section only when at least one card is available.
const STORAGE_KEY = "lolonaa:recently-viewed";
const MAX_STORED = 12;

const readHandles = () => {
  try {
    const list = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(list) ? list.filter((h) => typeof h === "string") : [];
  } catch {
    return [];
  }
};

const writeHandles = (list) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_STORED)));
  } catch {
    /* storage unavailable (private mode): nothing to remember */
  }
};


// Backup card when the ?view=recent-card fragment isn't served: built from /products/<handle>.js.
const formatMoney = (cents, format) => {
  const amount = (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const noDecimals = Math.round(cents / 100).toLocaleString("en-US");
  return (format || "{{amount}}")
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimals)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace(/,/g, "#").replace(".", ",").replace(/#/g, "."))
    .replace(/\{\{\s*amount\s*\}\}/, amount);
};

const escapeHtml = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

const buildBackupCard = (product, host) => {
  if (!product?.available) return null;
  const li = document.createElement("li");
  li.dataset.handle = product.handle;
  const image = product.featured_image ? `${product.featured_image.replace(/^\/\//, "https://")}${product.featured_image.includes("?") ? "&" : "?"}width=600` : "";
  const price = formatMoney(product.price, host.dataset.moneyFormat);
  const compare = product.compare_at_price > product.price ? formatMoney(product.compare_at_price, host.dataset.moneyFormat) : "";
  const discount = compare ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100) : 0;
  const badge = discount ? (host.dataset.saleBadge || "-{{ percent }}%").replace(/\{\{\s*percent\s*\}\}/, discount) : "";
  li.innerHTML = `
    <a href="${escapeHtml(product.url)}" class="group/card flex h-full flex-col gap-2 no-underline">
      <span class="relative block aspect-square overflow-hidden rounded-card bg-surface-secondary">
        ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy" class="absolute inset-0 size-full object-cover">` : ""}
        ${badge ? `<span class="absolute top-2 start-2 rounded-button bg-interactive px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-interactive-text">${escapeHtml(badge)}</span>` : ""}
      </span>
      <span class="text-sm font-medium text-on-surface line-clamp-2">${escapeHtml(product.title)}</span>
      <span class="flex flex-wrap items-baseline gap-2 text-sm font-medium text-on-surface">
        <span>${escapeHtml(price)}</span>${compare ? `<s class="text-xs text-on-surface-secondary">${escapeHtml(compare)}</s>` : ""}
      </span>
      <span class="mt-auto inline-flex h-10 items-center justify-center rounded-button border border-accent px-2 text-xs font-medium text-accent group-hover/card:bg-accent group-hover/card:text-white sm:text-sm">${escapeHtml(host.dataset.viewLabel || "View")}</span>
    </a>`;
  return li;
};

class SRecentlyViewedTrack extends HTMLElement {
  connectedCallback() {
    const handle = this.dataset.handle;
    if (!handle) return;
    writeHandles([handle, ...readHandles().filter((h) => h !== handle)]);
  }
}

// One request per product per page view, shared by every <s-recently-viewed> on the page (drawer + cart page).
const cardCache = new Map();

const loadCard = (handle, host) => {
  if (!cardCache.has(handle)) {
    cardCache.set(
      handle,
      (async () => {
        try {
          const root = window.Shopify?.routes?.root || "/";
          const base = `${root}products/${encodeURIComponent(handle)}`;
          const response = await fetch(`${base}?view=recent-card`, { headers: { Accept: "text/html" } });
          if (response.ok) {
            const doc = new DOMParser().parseFromString(await response.text(), "text/html");
            const card = doc.querySelector("[data-recent-card]");
            if (card) return card.dataset.available === "true" ? card : null;
          }
          const json = await fetch(`${base}.js`, { headers: { Accept: "application/json" } });
          return json.ok ? buildBackupCard(await json.json(), host) : null;
        } catch {
          return null;
        }
      })(),
    );
  }
  return cardCache.get(handle);
};

class SRecentlyViewed extends HTMLElement {
  // The cart drawer is refreshed in place after every cart change, which puts back the server markup
  // (hidden + empty list) without reconnecting this element. Watch for that and refill.
  static observedAttributes = ["hidden", "data-exclude"];

  #rendering = false;
  #queued = false;
  #rerun = false;

  connectedCallback() {
    this.#observer = new MutationObserver(() => this.#schedule());
    const list = this.querySelector("[data-recent-list]");
    if (list) this.#observer.observe(list, { childList: true });
    this.#schedule();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#schedule();
  }

  #observer = null;

  #schedule() {
    if (this.#queued) return;
    this.#queued = true;
    queueMicrotask(() => {
      this.#queued = false;
      this.#render();
    });
  }

  async #render() {
    if (this.#rendering) {
      this.#rerun = true;
      return;
    }
    const list = this.querySelector("[data-recent-list]");
    if (!list) return;
    const exclude = new Set((this.dataset.exclude || "").split(",").filter(Boolean));
    const limit = parseInt(this.dataset.limit, 10) || 8;
    const handles = readHandles().filter((h) => !exclude.has(h));

    this.#rendering = true;
    try {
      const cards = await Promise.all(handles.map((handle) => loadCard(handle, this)));
      const available = cards.filter(Boolean).slice(0, limit);
      const key = available.map((card) => card.dataset.handle).join(",");
      const current = [...list.children].map((li) => li.dataset.handle).join(",");

      if (!available.length) {
        if (list.children.length) list.replaceChildren();
        if (!this.hidden) this.hidden = true;
        return;
      }
      if (current !== key) {
        list.replaceChildren(...available.map((card) => document.importNode(card, true)));
      }
      if (this.hidden) this.hidden = false;
    } finally {
      this.#rendering = false;
      if (this.#rerun) {
        this.#rerun = false;
        this.#schedule();
      }
    }
  }
}

if (!customElements.get("s-recently-viewed-track")) customElements.define("s-recently-viewed-track", SRecentlyViewedTrack);
if (!customElements.get("s-recently-viewed")) customElements.define("s-recently-viewed", SRecentlyViewed);
